/**
 * WebhookService - Pipeline Webhook Notifications
 *
 * NOTA: A partir desta versão, os webhooks do pipeline são disparados automaticamente
 * pelo backend através do sistema de eventos (PipelineWebhookListener).
 *
 * Este serviço agora serve como:
 * 1. Interface de compatibilidade para código legado que ainda chama estes métodos
 * 2. Logger para debugging durante a transição
 *
 * Os webhooks do pipeline podem ser configurados em:
 * Settings > Integrations > Webhooks
 *
 * Eventos suportados pelo backend:
 * - pipeline_card_created
 * - pipeline_card_updated
 * - pipeline_card_deleted
 * - pipeline_card_stage_changed
 * - pipeline_card_won
 * - pipeline_card_lost
 * - pipeline_card_owner_changed
 */

class WebhookService {
  constructor() {
    // DEPRECATED: localStorage não é mais usado para configuração de webhooks
    // A configuração agora é feita via Settings > Webhooks (backend)
    this.isBackendManaged = true;
  }

  /**
   * @deprecated Use Settings > Webhooks para configurar webhooks
   */
  // eslint-disable-next-line class-methods-use-this
  setWebhookConfig({ url, enabled }) {
    // DEPRECATED: Mantém compatibilidade temporária com código legado
    // Configure webhooks em Settings > Integrations > Webhooks
    localStorage.setItem('pipeline_webhook_url', url);
    localStorage.setItem('pipeline_webhook_enabled', enabled);
  }

  // eslint-disable-next-line class-methods-use-this
  formatCardData(cardData) {
    if (!cardData) {
      return null;
    }

    return {
      id: cardData.id,
      account_id: cardData.account_id,
      conversation_display_id: cardData.conversation_display_id,
      pipeline_id: cardData.pipeline_id,
      pipeline_stage: cardData.pipeline_stage,
      stage_entered_at: cardData.stage_entered_at,
      position: cardData.position,
      title: cardData.title,
      description: cardData.description,
      lead_score: cardData.lead_score,
      owner_id: cardData.owner_id,
      contact_id: cardData.contact_id,
      custom_attributes: cardData.custom_attributes || {},
      item_details: cardData.item_details || {},
      timer_started_at: cardData.timer_started_at,
      timer_duration: cardData.timer_duration,
      created_at: cardData.created_at,
      updated_at: cardData.updated_at,
    };
  }

  /**
   * Notifica mudança de estágio
   * @note Webhooks são disparados automaticamente pelo backend via PipelineWebhookListener
   */
  // eslint-disable-next-line class-methods-use-this, no-unused-vars
  async notifyStageChange(_cardData, _fromStage, _toStage) {
    // Backend dispara automaticamente via after_commit callback
    // Método mantido para compatibilidade com código legado
  }

  /**
   * Notifica atualização de card
   * @note Webhooks são disparados automaticamente pelo backend via PipelineWebhookListener
   */
  // eslint-disable-next-line class-methods-use-this, no-unused-vars
  async notifyCardUpdated(_cardData, _changes = {}) {
    // Backend dispara automaticamente via after_commit callback
    // Método mantido para compatibilidade com código legado
  }

  /**
   * Notifica criação de card
   * @note Webhooks são disparados automaticamente pelo backend via PipelineWebhookListener
   */
  // eslint-disable-next-line class-methods-use-this, no-unused-vars
  async notifyCardCreated(_cardData) {
    // Backend dispara automaticamente via after_commit callback
    // Método mantido para compatibilidade com código legado
  }

  /**
   * Notifica exclusão de card
   * @note Webhooks são disparados automaticamente pelo backend via PipelineWebhookListener
   */
  // eslint-disable-next-line class-methods-use-this, no-unused-vars
  async notifyCardDeleted(_cardData) {
    // Backend dispara automaticamente via after_commit callback
    // Método mantido para compatibilidade com código legado
  }

  /**
   * Notifica início do timer
   * @note Timer events não são cobertos pelo backend webhook ainda
   */
  // eslint-disable-next-line class-methods-use-this, no-unused-vars
  async notifyTimerStarted(_cardData) {
    // Timer events não são cobertos pelo backend webhook ainda
    // Método mantido para compatibilidade com código legado
  }

  /**
   * Notifica parada do timer
   * @note Timer events não são cobertos pelo backend webhook ainda
   */
  // eslint-disable-next-line class-methods-use-this, no-unused-vars
  async notifyTimerStopped(_cardData, _duration) {
    // Timer events não são cobertos pelo backend webhook ainda
    // Método mantido para compatibilidade com código legado
  }

  /**
   * Notifica reordenação de cards
   * @note Reorder events não são cobertos pelo backend webhook ainda
   */
  // eslint-disable-next-line class-methods-use-this, no-unused-vars
  async notifyCardReordered(_cards, _changes) {
    // Reorder events não são cobertos pelo backend webhook ainda
    // Método mantido para compatibilidade com código legado
  }

  /**
   * Notifica deal ganho
   * @note Webhooks são disparados automaticamente pelo backend via PipelineWebhookListener
   */
  // eslint-disable-next-line class-methods-use-this, no-unused-vars
  async notifyDealWon(_cardData) {
    // Backend dispara automaticamente via after_commit callback
    // Método mantido para compatibilidade com código legado
  }

  /**
   * Notifica deal perdido
   * @note Webhooks são disparados automaticamente pelo backend via PipelineWebhookListener
   */
  // eslint-disable-next-line class-methods-use-this, no-unused-vars
  async notifyDealLost(_cardData) {
    // Backend dispara automaticamente via after_commit callback
    // Método mantido para compatibilidade com código legado
  }
}

export default new WebhookService();
